import React, { useEffect, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import Loader from "./Loader"; // ✅ import global loader

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:1010";

const SponsorshipModal = ({ selectedPackage, price, onClose }) => {
  const [showPayment, setShowPayment] = useState(false);
  const [sponsorPayload, setSponsorPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  const paypalRef = useRef(null);

  const validationSchema = Yup.object({
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    company: Yup.string().required("Company Name is required"),
  });

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) return resolve();
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = (e) => reject(e);
      document.body.appendChild(script);
    });

  // create booking on backend
  const createSponsorshipBooking = async (payload, paymentMethod = null) => {
    const body = {
      type: "sponsorship",
      sponsorship: {
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        company: payload.company,
        package: selectedPackage,
      },
      total: Number(price),
      paymentMethod,
    };

    const res = await axios.post(`${API_BASE}/bookings`, body);
    if (!res.data?.success) throw new Error(res.data?.error || "Failed to create booking");
    return res.data.booking;
  };

  // PayPal SDK & buttons
  useEffect(() => {
    if (selectedGateway !== "paypal" || !booking) return;

    const clientIdEnv =
      process.env.REACT_APP_PAYPAL_CLIENT_ID || process.env.REACT_APP_PAYPAL_KEY_ID || "";
    if (!clientIdEnv) {
      setError("Missing PayPal client id in frontend env (set REACT_APP_PAYPAL_CLIENT_ID).");
      return;
    }

    const currency = booking.currency || "USD";
    const sdkUrl = `https://www.paypal.com/sdk/js?client-id=${clientIdEnv}&currency=${currency}&intent=capture`;

    loadScript(sdkUrl)
      .then(() => renderPayPalButtons())
      .catch((err) => {
        console.error("Failed to load PayPal SDK", err);
        setError("Failed to load PayPal SDK");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGateway, booking]);

  const renderPayPalButtons = () => {
    if (!window.paypal || !paypalRef.current) {
      setError("PayPal SDK not available");
      return;
    }
    paypalRef.current.innerHTML = "";

    window.paypal
      .Buttons({
        style: { layout: "vertical", color: "gold", shape: "rect", label: "paypal" },
        createOrder: async () => {
          const resp = await axios.post(`${API_BASE}/payment/paypal/create-order`, {
            bookingId: booking._id,
          });
          const orderId = resp.data?.id || resp.data?.orderID;
          if (!orderId) throw new Error("PayPal create-order returned no id");
          return orderId;
        },
        onApprove: async (data) => {
          setLoading(true);
          try {
            const captureRes = await axios.post(`${API_BASE}/payment/paypal/capture-order`, {
              orderId: data.orderID,
              bookingId: booking._id,
            });
            if (captureRes.data?.success) {
              alert("✅ PayPal payment successful — sponsorship confirmed!");
              onClose && onClose({ success: true, bookingId: booking._id });
            } else {
              console.error("PayPal capture response", captureRes.data);
              alert("PayPal verification failed. Contact support.");
            }
          } catch (err) {
            console.error("PayPal capture error", err);
            alert("PayPal capture failed. Check console.");
          } finally {
            setLoading(false);
          }
        },
        onError: (err) => {
          console.error("PayPal buttons error", err);
          alert("PayPal error. Try again.");
        },
      })
      .render(paypalRef.current);
  };

  // Razorpay flow
  const handleRazorpay = async () => {
    setError(null);
    try {
      setLoading(true);
      let b = booking;
      if (!b) {
        b = await createSponsorshipBooking(sponsorPayload || {}, "razorpay");
        setBooking(b);
      }

      const orderRes = await axios.post(`${API_BASE}/payment/razorpay/create-order`, {
        bookingId: b._id,
      });
      const order = orderRes.data;
      if (!order || !order.id) throw new Error("Razorpay create-order failed");

      await loadScript("https://checkout.razorpay.com/v1/checkout.js");
      if (!window.Razorpay) throw new Error("Razorpay SDK failed to load");

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || "",
        amount: order.amount,
        currency: order.currency,
        name: "ARO Events",
        description: `Sponsorship ${b._id}`,
        order_id: order.id,
        handler: async (response) => {
          setLoading(true);
          try {
            const verifyRes = await axios.post(
              `${API_BASE}/payment/razorpay/verify-payment`,
              {
                razorpay_order_id: order.id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                bookingId: b._id,
                amount: order.amount,
              }
            );

            if (verifyRes.data?.success) {
              alert("✅ Razorpay payment successful — sponsorship confirmed!");
              onClose && onClose({ success: true, bookingId: b._id });
            } else {
              console.error("Razorpay verification failed", verifyRes.data);
              alert("Razorpay verification failed. Contact support.");
            }
          } catch (err) {
            console.error("Razorpay handler error", err);
            alert("Razorpay verification error. See console.");
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: b.sponsorship.firstName + " " + b.sponsorship.lastName,
          email: b.sponsorship.email,
        },
        notes: { package: selectedPackage },
        theme: { color: "#3399cc" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error("Razorpay flow error", err);
      setError(err.message || "Razorpay flow failed");
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setError(null);
    setLoading(true);
    try {
      const created = await createSponsorshipBooking(values, null);
      setSponsorPayload(values);
      setBooking(created);
      setShowPayment(true);
      setSelectedGateway(null);
    } catch (err) {
      console.error("Create sponsorship booking error", err);
      setError(err.message || "Failed to create sponsorship booking");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!showPayment && paypalRef.current) paypalRef.current.innerHTML = "";
  }, [showPayment]);

  return (
    <div style={styles.overlay}>
      {loading && <Loader />} {/* ✅ show loader overlay */}
      <motion.div
        style={styles.modal}
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <button style={styles.closeBtn} onClick={() => onClose && onClose()}>
          ✖
        </button>
        <h2 style={styles.heading}>
          {selectedPackage} Package – {booking?.currency || "USD"} {price}
        </h2>

        <AnimatePresence mode="wait">
          {!showPayment ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Formik
                initialValues={{ firstName: "", lastName: "", email: "", company: "" }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {() => (
                  <Form style={styles.form}>
                    <div style={styles.inputGroup}>
                      <Field name="firstName" placeholder="First Name *" style={styles.input} />
                      <ErrorMessage name="firstName" component="div" style={styles.error} />
                    </div>
                    <div style={styles.inputGroup}>
                      <Field name="lastName" placeholder="Last Name *" style={styles.input} />
                      <ErrorMessage name="lastName" component="div" style={styles.error} />
                    </div>
                    <div style={styles.inputGroup}>
                      <Field name="email" placeholder="Email address *" style={styles.input} />
                      <ErrorMessage name="email" component="div" style={styles.error} />
                    </div>
                    <div style={styles.inputGroup}>
                      <Field name="company" placeholder="Company Name *" style={styles.input} />
                      <ErrorMessage name="company" component="div" style={styles.error} />
                    </div>
                    <button type="submit" style={styles.submitBtn} disabled={loading}>
                      {loading ? "Saving..." : "Continue to Payment"}
                    </button>
                    {error && <div style={{ color: "red", marginTop: 8 }}>{error}</div>}
                  </Form>
                )}
              </Formik>
            </motion.div>
          ) : (
            <motion.div
              key="payment"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <h4 style={{ marginBottom: "15px", textAlign: "center" }}>
                Choose Payment Method
              </h4>

              {error && <div style={{ color: "red", marginBottom: 8 }}>{error}</div>}

              <div style={styles.paymentOptions}>
                <button
                  onClick={() => {
                    setSelectedGateway("paypal");
                  }}
                  style={{ ...styles.payBtn, marginRight: 8 }}
                >
                  PayPal
                </button>

                <button
                  onClick={() => {
                    setSelectedGateway("razorpay");
                    handleRazorpay();
                  }}
                  disabled={loading}
                  style={styles.payBtn}
                >
                  {loading ? "Processing..." : "Razorpay"}
                </button>
              </div>

              {selectedGateway === "paypal" && (
                <div ref={paypalRef} style={{ marginTop: 20 }}></div>
              )}

              <div style={{ marginTop: 12, textAlign: "center" }}>
                <button
                  onClick={() => setShowPayment(false)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#666",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  Edit info
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modal: {
    background: "#fff",
    padding: "30px",
    borderRadius: "16px",
    width: "400px",
    position: "relative",
    fontFamily: "'Inter', sans-serif",
    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
  },
  closeBtn: {
    position: "absolute",
    top: "12px",
    right: "12px",
    background: "none",
    border: "none",
    fontSize: "18px",
    cursor: "pointer",
  },
  heading: { marginBottom: "20px", fontSize: "20px", fontWeight: "600", textAlign: "center" },
  form: { display: "flex", flexDirection: "column", gap: "15px" },
  inputGroup: { display: "flex", flexDirection: "column" },
  input: { padding: "12px", borderRadius: "8px", border: "1px solid #ddd", fontSize: "14px" },
  error: { color: "red", fontSize: "12px", marginTop: "5px" },
  submitBtn: {
    background: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
    color: "#fff",
    border: "none",
    padding: "12px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600",
  },
  paymentOptions: { display: "flex", gap: "12px", justifyContent: "center", marginTop: "8px" },
  payBtn: {
    flex: 1,
    padding: "12px",
    borderRadius: "8px",
    fontWeight: "600",
    cursor: "pointer",
    border: "1px solid #ccc",
  },
};

export default SponsorshipModal;
